import { useState, useRef } from 'react';
import { Modal, Input, Tag, Space, Select } from 'antd';
import type { InputRef } from 'antd';

interface PromptOptionsBase {
    value?: string;
    placeholder?: string;
    title?: string;
    okText?: string;
    cancelText?: string;
    tags?: string[];
}

interface PromptOptionsSingle extends PromptOptionsBase {
    multiple?: false;
}

interface PromptOptionsMultiple extends PromptOptionsBase {
    multiple: true;
}

type PromptOptions = PromptOptionsSingle | PromptOptionsMultiple;

export function usePrompt() {
    const [value, setValue] = useState('');
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [multiple, setMultiple] = useState(false);
    const [title, setTitle] = useState('');
    const [okText, setOkText] = useState('');
    const [cancelText, setCancelText] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [placeholder, setPlaceholder] = useState('');

    const [isOpen, setIsOpen] = useState(false);
    const onCloseRef = useRef<null | ((result: string | string[]) => void)>(null);
    const inputRef = useRef<InputRef>(null);

    const closeAndResolve = (result: string | string[]) => {
        setValue('');
        setSelectedValues([]);
        setTags([]);
        setMultiple(false);
        setIsOpen(false);
        if (onCloseRef.current) {
            onCloseRef.current(result);
            onCloseRef.current = null;
        }
    };

    const handleCancel = () => {
        closeAndResolve(multiple ? [] : '');
    };

    const handleOk = () => {
        if (multiple) {
            if (selectedValues.length === 0) {
                return;
            }
            closeAndResolve(selectedValues);
            return;
        }

        if (!value) {
            return;
        }
        closeAndResolve(value);
    };

    const handleTagClick = (tag: string) => {
        if (multiple) {
            setSelectedValues((prev) => (
                prev.includes(tag)
                    ? prev.filter((t) => t !== tag)
                    : [...prev, tag]
            ));
            return;
        }

        setValue(tag);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    async function prompt(options: PromptOptionsMultiple): Promise<string[]>;
    async function prompt(options?: PromptOptionsSingle): Promise<string>;
    async function prompt({
        value = '',
        title = 'Подтверждение',
        okText = 'ОК',
        cancelText = 'Отмена',
        tags = [],
        placeholder = '',
        multiple: isMultiple = false,
    }: PromptOptions = {}): Promise<string | string[]> {
        setIsOpen(true);
        setValue(value);
        setSelectedValues([]);
        setMultiple(isMultiple);
        setTitle(title);
        setOkText(okText);
        setCancelText(cancelText);
        setTags(tags);
        setPlaceholder(placeholder);

        requestAnimationFrame(() => {
            if (!isMultiple && inputRef.current) {
                inputRef.current.focus();
            }
        });

        return new Promise<string | string[]>((resolve) => {
            onCloseRef.current = (result) => resolve(result);
        });
    }

    return {
        prompt,

        promptModal: (
            <Modal
                title={title}
                open={isOpen}
                onOk={handleOk}
                onCancel={handleCancel}
                okText={okText}
                cancelText={cancelText}
                zIndex={3500}
                okButtonProps={{
                    disabled: multiple ? selectedValues.length === 0 : !value,
                }}
            >
                {multiple ? (
                    <Select
                        mode="tags"
                        style={{ width: '100%' }}
                        value={selectedValues}
                        onChange={setSelectedValues}
                        placeholder={placeholder || 'Выберите или введите теги'}
                        options={tags.map((tag) => ({ label: tag, value: tag }))}
                        autoFocus
                    />
                ) : (
                    <Input
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && handleOk()}
                        placeholder={placeholder}
                        ref={inputRef}
                    />
                )}
                {tags.length > 0 && (
                    <Space wrap style={{ marginTop: 8 }} size={2}>
                        {tags.map((tag, index) => {
                            const isActive = multiple
                                ? selectedValues.includes(tag)
                                : tag === value;

                            return (
                                <Tag
                                    key={index}
                                    onClick={() => handleTagClick(tag)}
                                    style={{ cursor: 'pointer' }}
                                    color={isActive ? 'blue' : undefined}
                                >
                                    {tag}
                                </Tag>
                            );
                        })}
                    </Space>
                )}
            </Modal>
        ),
    };
}
